<template>
  <div class="flex flex-wrap -mx-4 static">
    <div
      v-for="(colItems, colIndex) in renderedColumns"
      :key="colIndex"
      class="static px-4 same-height-columns"
      v-bind="columnStyling"
    >
      <div
        ref="columns"
        class="block"
      >
        <component
          :is="items[itemIndex]"
          v-for="itemIndex in colItems"
          :key="itemIndex"
        />
      </div>
    </div>
  </div>
</template>

<script>
import screenWidthMixin from '@packages/screen/mixins/screen-width.mixin.js'
import { useStyleTag } from '@vueuse/core'

export default defineNuxtComponent({
  setup (props) {
    const tailwindBreakpointsPx = getTailwindBreakpointsPx()

    if (typeof props.columns === 'object') {
      useStyleTag(
        Object.entries(props.columns).map(([breakpoint, columns]) => `
          @media (min-width: ${tailwindBreakpointsPx[breakpoint]}px) {
            .same-height-columns {
              width: ${100 / columns}%;
            }
          }
        `).join('')
      )
    }
  },

  mixins: [screenWidthMixin],

  props: {
    /**
     * @prop {
     *  columns | { [breakpoint]: columns }
     * } columns
     * Based on bootstraps grid system (1-12)
     * Avoid using uneven numbers
     */
    columns: {
      type: [Number, Object],
      default: 2,
    },
  },

  data () {
    return {
      // [columnIndex: [columnItemIndices]] - Obs. columnItemIndices starts at 1
      renderedColumns: [],
      columnCount: 0,
      prevItemsCount: 0,
      prevBreakpoint: null,
    }
  },

  computed: {
    isResponsive () {
      return typeof this.columns !== 'number'
    },

    columnStyling () {
      return {
        style: {
          width: this.isResponsive
            ? null
            : (100 / this.columns) + '%',
        },
      }
    },

    items () {
      return unwrapSlot(this.$slots.default?.())
    },
  },

  watch: {
    renderedColumns: {
      handler () {
        this.$nextTick(this.distributeColumns)
      },
      deep: true,
    },
  },

  updated () {
    if (this.prevItemsCount !== this.items.length) {
      this.prevItemsCount = this.items.length

      this.$nextTick(this.distributeColumns)
    }

    if (this.isResponsive) {
      const closestBreakpoint = this.getClosestBreakpoint(Object.keys(this.columns))

      if (this.prevBreakpoint !== closestBreakpoint) {
        this.prevBreakpoint = this.getClosestBreakpoint(Object.keys(this.columns))

        this.getColumnCount()
        this.$nextTick(this.distributeColumns)
      }
    }
  },

  created () {
    this.prevItemsCount = this.items.length
  },

  mounted () {
    this.getColumnCount()
  },

  methods: {
    getColumnCount () {
      this.columnCount = this.isResponsive
        ? this.columns[this.getClosestBreakpoint(Object.keys(this.columns))]
        : this.columns

      if (this.columnCount !== this.renderedColumns.length) {
        this.renderedColumns = new Array(this.columnCount).fill(null).map(() => [])
      }
    },

    distributeColumns () {
      if (!process.browser) {
        return
      }

      const nextItemIndex = this.renderedColumns.reduce((value, col) => value + col.length, 0)
      if (this.items.length - 1 < nextItemIndex) {
        return
      }

      if (nextItemIndex <= this.renderedColumns.length - 1) {
        this.renderedColumns[nextItemIndex]?.push(nextItemIndex)
        return
      }

      let shortestColumnIndex
      for (let columnIndex = 0; columnIndex < this.columnCount; columnIndex++) {
        if (
          !this.$refs.columns?.[shortestColumnIndex] ||
          this.$refs.columns?.[columnIndex]?.offsetHeight < this.$refs.columns?.[shortestColumnIndex]?.offsetHeight
        ) {
          shortestColumnIndex = columnIndex
        }
      }

      this.renderedColumns[shortestColumnIndex]?.push(nextItemIndex)
    },
  },
})
</script>